import { chartColors, getCategoryColor as getDepartmentCategoryColor } from '../config/analyticsCharts';

// Static mapping of common categories to specific colors
// This ensures consistent coloring across the application
export const categoryColorMap: Record<string, string> = {
  // Appointment types
  'First Visit': '#1E88E5', // Blue
  'Follow-up < 15 days': '#43A047',   // Green
  'Follow-up > 15 days': '#8E24AA', // Purple
  'Session': '#FFB300', // Amber
  'Follow up': '#43A047',   // legacy label
  'Free Consultation': '#FFB300', // Amber
  
  // Insurance types
  'IPM': '#1E88E5', // Blue
  'PAF': '#8E24AA', // Purple
  'PAL': '#43A047', // Green
  'Personnel': '#FFB300', // Amber
  
  // Sex categories
  'Male': '#1E88E5', // Blue
  'Female': '#E53935', // Red

  // Common test types
  'Blood Test': '#1E88E5', // Blue
  'X-Ray': '#8E24AA',      // Purple
  'Ultrasound': '#43A047', // Green
  'MRI': '#FFB300',        // Amber
  
  // Other common categories
  'Yes': '#43A047', // Green
  'No': '#E53935',  // Red
};

// Map to remember dynamically assigned colors
const dynamicColorMap: Record<string, string> = {};

/**
 * Gets a consistent color for a category name
 * @param categoryName The name of the category (appointment type, insurance, etc.)
 * @returns A hex color code
 */
export const getCategoryColor = (categoryName: string): string => {
  // Skip empty or undefined categories
  if (!categoryName) {
    return chartColors.seriesColors[0]; // Default blue
  }
  
  // 1. Try exact match from predefined categories
  if (categoryColorMap[categoryName]) {
    return categoryColorMap[categoryName];
  }
  
  // 2. Try case-insensitive match from predefined categories
  const lowerName = categoryName.toLowerCase();
  const predefinedKey = Object.keys(categoryColorMap)
    .find(key => key.toLowerCase() === lowerName);
    
  if (predefinedKey) {
    return categoryColorMap[predefinedKey];
  }

  if (dynamicColorMap[categoryName]) {
    return dynamicColorMap[categoryName];
  }

  const color = getDepartmentCategoryColor(categoryName);
  dynamicColorMap[categoryName] = color;
  return color;
};

/**
 * Gets a set of colors for an array of categories
 * @param categoryNames Array of category names
 * @returns Array of corresponding colors
 */
export const getCategoryColors = (categoryNames: string[]): string[] => {
  return categoryNames.map(name => getCategoryColor(name));
};

/**
 * One colour per name, guaranteed distinct within the chart (SHER-853).
 *
 * getCategoryColor answers per name and so cannot see siblings. Its last resort is
 * `firstChar.charCodeAt(0) % palette.length`, which gives every name sharing a first
 * letter the same colour: the Finances "Total Discounts" card plots Total Billing, Total
 * Discounts and Total Income, and rendered all three in the same red. Laboratory's "Test
 * by Type" collides the same way on Cholesterol HDL / LDL / Total.
 *
 * Names that resolve to an explicit colour keep it -- departments must stay the same
 * colour on every chart of the tab (SHER-280) -- and only a name that would repeat a
 * colour already taken in THIS chart is moved to the next free palette slot. So a chart
 * whose colours are already distinct is byte-identical to before, and the palette is only
 * reordered where it was previously ambiguous.
 *
 * With more names than palette entries a repeat is unavoidable; the extras then cycle the
 * palette rather than dropping out, because a series with no colour does not render.
 */
export const getDistinctCategoryColors = (categoryNames: string[]): string[] => {
  if (!Array.isArray(categoryNames)) {
    return [];
  }
  const palette = chartColors.seriesColors;
  const taken = new Set<string>();

  return categoryNames.map((name, index) => {
    const preferred = getCategoryColor(name);
    if (!taken.has(preferred)) {
      taken.add(preferred);
      return preferred;
    }
    const free = palette.find((color) => !taken.has(color));
    if (free) {
      taken.add(free);
      return free;
    }
    return palette[index % palette.length];
  });
};

/** Colors for Apex distributed bars: one color per x-axis category (department). */
export const getDistributedBarColors = (categories: string[]): string[] => {
  return getDistinctCategoryColors(categories);
};